/**
 * The BrainBit Pro montage, exactly as the point-cloud asset records it.
 *
 * Constants only: no arithmetic, no React, no payload. Everything the brain
 * scan knows about electrodes lives here, so `cloud.ts` can assert the asset
 * agrees with it and `SensorList` can name the pairs before the canvas chunk
 * has loaded.
 *
 * The asset names the temporal pair `TP7` / `TP8`; the device's own channel
 * names for the same electrodes are `T7` / `T8`
 * (`brainspot-eeg/packages/eeg-tracking/eeg_tracking/config.py`). The asset's
 * spelling wins here because the canvas draws the asset -- and `parseCloud`
 * pins these names, so a re-exported cloud that renamed them fails loudly
 * rather than shipping a hidden list that disagrees with what is painted.
 */

export type RegionId = 1 | 2 | 3 | 4

export interface Sensor {
  readonly name: string
  readonly region: RegionId
}

/**
 * In asset order. `parseCloud` compares the asset's eight electrodes against
 * this set, so the two can never drift apart silently.
 */
export const SENSORS: readonly Sensor[] = [
  { name: 'Fp1', region: 1 },
  { name: 'Fp2', region: 1 },
  { name: 'TP7', region: 2 },
  { name: 'TP8', region: 2 },
  { name: 'P7', region: 3 },
  { name: 'P8', region: 3 },
  { name: 'O1', region: 4 },
  { name: 'O2', region: 4 },
] as const

export const REGION_NAMES: Readonly<Record<RegionId, string>> = {
  1: 'Frontal',
  2: 'Temporal',
  3: 'Parietal',
  4: 'Occipital',
}

/**
 * THE MONTAGE'S ANATOMICAL KEY, AND CANVAS INK ONLY.
 *
 * These four triples say WHERE an electrode sits on the head. They are not a
 * status palette and they carry no reading of any kind. They live as literals
 * in this module, appear in no stylesheet, and are never used on a pill, a
 * label, a chip or a score -- `sourceGates.test.ts` V6 proves all three.
 */
export const REGION_RGB: Readonly<Record<RegionId, readonly [number, number, number]>> = {
  1: [245, 168, 56],
  2: [237, 82, 144],
  3: [76, 209, 118],
  4: [87, 160, 245],
}

/** The one line the popover carries besides the sensor's own name. U+00B7. */
export const COVERAGE_LINE = 'EEG coverage · BrainBit Pro · not a medical device'

/** `TP8 · Temporal`. The only sentence this component ever writes about a sensor. */
export function sensorLabel(sensor: Sensor): string {
  return `${sensor.name} · ${REGION_NAMES[sensor.region]}`
}

/**
 * Canvas text cannot read a `var()`, so the two faces are restated here. The
 * family names must stay equal to `src/styles/tokens/fonts.css:24,40`, and the
 * renderer waits on `document.fonts.load` for both before its last static
 * frame -- `font-display: swap` otherwise leaves a single frame painted in the
 * fallback face forever.
 */
export const CHIP_FONT = "600 10px 'DM Sans', system-ui, sans-serif"
export const GUIDE_FONT = "700 8px 'JetBrains Mono', ui-monospace, monospace"
